import React, { useContext, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import NewsArticleCard from './NewsArticleCard';
import { Dropdown } from '../common/Dropdown';
import { Grid } from '../common/Grid';
import LayoutContext, { MODERN_LAYOUT_COLLAPSE_WIDTH } from '../../contexts/Layout';
import ComponentTitle from '../common/ComponentTitle';
import Utils from '../../common/Utils';

const NEWS_ARTICLE_GRID_FILTER_OPTIONS = [
    {
        label: 'Most Recent',
        value: 'Most Recent'
    },
    {
        label: 'Oldest First',
        value: 'Oldest First'
    },
    {
        label: 'Featured',
        value: 'Featured'
    }
];

const NewsArticleGrid = ({
    articleCategory,
    author,
    heading,
    initialLoadCount = 4,
    ministryGroup,
    newsArticles = [],
    nodeTypeAlias = 'NewsArticleGrid',
    references = {},
    width,
    ...rest
}) => {
    const { layout } = useContext(LayoutContext);
    const [filter, setFilter] = useState(0);

    const sortArticles = articles => {
        return articles.sort((a, b) => {
            if (filter === 0) {
                const aDate = new Date(a?.releaseDate);
                const bDate = new Date(b?.releaseDate);

                if (aDate > bDate) {
                    return -1;
                }

                if (aDate < bDate) {
                    return 1;
                }

                return 0;
            } else if (filter === 1) {
                const aDate = new Date(a?.releaseDate);
                const bDate = new Date(b?.releaseDate);

                if (aDate > bDate) {
                    return 1;
                }

                if (aDate < bDate) {
                    return -1;
                }

                return 0;
            } else {
                const aFeatured = a?.featured;
                const bFeatured = b?.featured;

                if (aFeatured) {
                    return 1;
                } else if (bFeatured) {
                    return -1;
                }

                return 0;
            }
        });
    };

    const newsGridItems = () => {
        const filteredArticles = newsArticles?.filter(article => {
            let include = true;

            if (ministryGroup?.length > 0) {
                if (Array.isArray(article.ministryGroup)) {
                    if (article.ministryGroup.length > 0) {
                        article.ministryGroup.forEach(ministry => {
                            if (ministryGroup.indexOf(ministry) === -1) {
                                include = false;
                            }
                        });
                    } else {
                        include = false;
                    }
                }
            }

            if (article.articleCategory?.length > 0) {
                if (Array.isArray(article.articleCategory)) {
                    if (article.articleCategory.length > 0) {
                        article.articleCategory.forEach(category => {
                            if (article.articleCategory.indexOf(category) === -1) {
                                include = false;
                            }
                        });
                    } else {
                        include = false;
                    }
                }
            }

            if (author?.length > 0) {
                if (!article.author.equals(author)) {
                    include = false;
                }
            }

            return include;
        });

        if (filteredArticles) {
            const sortedArticles = sortArticles(filteredArticles);

            return sortedArticles.slice(0, initialLoadCount).map((article, index) => {
                if (index + 1 <= initialLoadCount) {
                    const { key: articleKey, rest: articleRest } = Utils.splitKeyFromProps(article);

                    return (
                        <NewsArticleCard
                            {...articleRest}
                            key={articleKey}
                            categories={articleCategory}
                            style={{
                                borderColor: '#7f868b',
                                borderBottomWidth: StyleSheet.hairlineWidth,
                                borderRightWidth: index % 2 === 0 ? StyleSheet.hairlineWidth : 0
                            }}
                            references={references}
                            width={rest.hardWidth <= MODERN_LAYOUT_COLLAPSE_WIDTH ? rest.hardWidth : Math.floor(rest.hardWidth * 0.5)}
                        />
                    );
                }
            });
        }
    };

    return (
        <View
            style={{ marginBottom: layout.styleGuide.componentSeparator }}
            dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
        >
            <View style={{ alignItems: 'center', flexDirection: 'row', justifyContent: heading ? 'space-between' : 'flex-end' }}>
                {!!heading && <ComponentTitle title={heading} />}
                <View style={{ marginRight: layout.styleGuide.spacer1 }} dataSet={{ spacerRight: `StyleGuide.Spacer1(${layout.styleGuide.spacer1})` }}>
                    <Dropdown
                        iconColor='#153d6e'
                        label='Filter By:'
                        labelStyle={layout.styleGuide.p2}
                        onSelect={selection => setFilter(NEWS_ARTICLE_GRID_FILTER_OPTIONS.findIndex(val => val.label.equals(selection)))}
                        options={NEWS_ARTICLE_GRID_FILTER_OPTIONS}
                        selection={!layout.device.isMobile ? NEWS_ARTICLE_GRID_FILTER_OPTIONS[filter].label : undefined}
                        selectionStyle={StyleSheet.flatten(layout.styleGuide.p2, { color: '#153d6e' })}
                    />
                </View>
            </View>
            <Grid items={newsGridItems()} width={rest.hardWidth} />
        </View>
    );
};

NewsArticleGrid.propTypes = {
    heading: PropTypes.string,
    initialLoadCount: PropTypes.number,
    ministryGroup: PropTypes.arrayOf(PropTypes.string),
    newsArticles: PropTypes.arrayOf(
        PropTypes.shape({
            articleCategory: PropTypes.arrayOf(PropTypes.string),
            author: PropTypes.string,
            avatar: PropTypes.shape({
                cndImagePath: PropTypes.string
            }),
            featured: PropTypes.bool,
            image: PropTypes.shape({
                cndImagePath: PropTypes.string
            }),
            link: PropTypes.shape({
                internalKey: PropTypes.string,
                externalUrl: PropTypes.string,
                isExternal: PropTypes.bool
            }),
            ministryGroup: PropTypes.arrayOf(PropTypes.string),
            title: PropTypes.string,
            releaseDate: PropTypes.string
        })
    )
};

export default NewsArticleGrid;
